/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { DeviceModel, Settings } from '../../../core/device';
import { Channel } from '../../../core/entities';
import { FilteredGuideInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging/Log';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../core/navigation';
import { TScreen } from '../../../core/navigation/types';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { TChannelV2 } from '../../../core/types';
import { CcbSwitch } from '../../../core/utils/ccb';
import { isJumpChannelCcbSwitchEnabled } from '../../../core/utils/ccb/CcbSwitchHelper';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { onRepaintFilteredGuideAction } from '../../../store/guide/filteredguide/filteredguide.actions';
import {
    disableGhostModeFilteredGuide,
    resetFilteredGuideIndexes,
} from '../../../store/guide/filteredguide/filteredguide.reducers';
import { resetCategoriesFilterAction } from '../../../store/overlay/channeloptionsoverlay/channeloptionsoverlay.actions';
import { showGuideScreen } from '../../../store/screens/screens.reducers';
import { ClockTypes } from '../../Clock';
import { AntiburnOsd, Mousetrap, ScreenHeader } from '../../common';
import { useScreenController, useUpdateEffect } from '../../hooks';
import { NoNetworkOverlay } from '../../NoNetworkOverlay';
import { Whisper } from '../../Whisper';
import { FilteredGuideFooter } from './FilteredGuideFooter';
import FilteredGuideInteractiveLayer from './FilteredGuideInteractiveLayer/FilteredGuideInteractiveLayer';
import FilteredGuidePreview from './FilteredGuidePreview/FilteredGuidePreview';
import FilteredGuideTable from './FilteredGuideTable/FilteredGuideTable';
import { useFilteredGuideTimeout } from './hooks/useFilteredGuideTimeout';

import './FilteredGuide.scss';

const TAG: string = 'FilteredGuide';

const FilteredGuide = () => {
    const [allPossibleChannels, setAllPossibleChannels] = useState<Array<Channel | TChannelV2>>([]);
    const { filteredGuideTurboScroll } = useTivoSelector((state: RootState) => state.filteredGuide);
    const { isActive: isAntiburnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const settingsService = new Settings();
    const isVideoWindowEnabled = settingsService.isVideoWindowEnabled();

    const channelOptionsOverlay = useTivoSelector(
        (state: RootState) => state.channelOptionsOverlay,
    );

    const { remountCounter } = useTivoSelector((state: RootState) => state.filteredGuide);
    const antiburnWasEnabledRef = useRef<boolean>(false);
    const dispatch = useTivoDispatch();
    const isFilteredGuideEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294,
    );

    useFilteredGuideTimeout();
    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.FILTERED_GUIDE,
            screenController: ScreenControllerTypes.FILTERED_GUIDE_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS,
        },
    });

    const fetchAllChannels = useCallback(async (channelsTypeProp: string) => {
        const filteredGuideInteractor = new FilteredGuideInteractor();
        const fetchChannels = isJumpChannelCcbSwitchEnabled()
            ? filteredGuideInteractor.fetchChannelsV2
            : filteredGuideInteractor.fetchChannels;
        const channels = await fetchChannels(channelsTypeProp);
        if (!channels || channels.channels.length === 0) {
            Log.w(TAG, 'No channels available for the filtered guide.');
            return;
        }

        const fetchFilteredOutChannels = isJumpChannelCcbSwitchEnabled()
            ? filteredGuideInteractor.fetchFilteredOutChannelsV2
            : filteredGuideInteractor.fetchFilteredOutChannels;
        const filteredChannels = (await fetchFilteredOutChannels()) || [];

        setAllPossibleChannels([...channels.channels, ...filteredChannels]);
    }, []);

    useEffect(() => {
        Diagnostics.info(TAG, 'FilteredGuide mounted.');
        Log.i(TAG, 'FilteredGuide mounted.');

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            Diagnostics.info(TAG, 'FilteredGuide unmounted.');
            Log.i(TAG, 'FilteredGuide unmounted.');
            dispatch(resetFilteredGuideIndexes());
        };
    }, []);

    const [isCursorActive, setisCursorActive] = useState(false);

    const onCursorStateChangeCallback = (state) => {
        setisCursorActive(state);
        if (!isCursorActive) dispatch(disableGhostModeFilteredGuide());
    };

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    useEffect(() => {
        fetchAllChannels(channelOptionsOverlay.channelsType);
    }, [fetchAllChannels, channelOptionsOverlay.channelsType]);

    useUpdateEffect(() => {
        if (isAntiburnOsdActive) {
            antiburnWasEnabledRef.current = true;
        } else if (antiburnWasEnabledRef.current && !isAntiburnOsdActive) {
            antiburnWasEnabledRef.current = false;
            dispatch(resetFilteredGuideIndexes());
            dispatch(onRepaintFilteredGuideAction());
        }
    }, [isAntiburnOsdActive]);

    useLayoutEffect(() => {
        if (
            channelOptionsOverlay.categoriesType ===
            Locale.getString(LocaleKeys.GUIDE_CATEGORY_TYPES_ALL)
        ) {
            dispatch(resetCategoriesFilterAction());
            dispatch(showGuideScreen());
        }
    }, []);

    if (!isFilteredGuideEnabled) {
        return null;
    }

    return (
        <div
            data-testid="filtered-guide"
            className="filtered-guide"
            key={
                channelOptionsOverlay.channelsType +
                channelOptionsOverlay.categoriesType +
                remountCounter.toString()
            }
        >
            <ScreenHeader
                title={`${Locale.getString(LocaleKeys.GUIDE_MESSAGE)}: ${channelOptionsOverlay.channelsType.toUpperCase()}`}
                clockType={ClockTypes.DEFAULT_CLOCK}
            />
            <div className="filtered-guide-content-container">
                {filteredGuideTurboScroll && (
                    <Mousetrap
                        value={'filtered-guide-turboscroll-mousetrap'}
                        className="turboscroll-mousetrap"
                    />
                )}
                <FilteredGuideTable
                    category={channelOptionsOverlay.categoriesType.toLowerCase()}
                    channelsType={channelOptionsOverlay.channelsType}
                    allPossibleChannels={allPossibleChannels}
                />
                <div
                    className={`filtered-guide-preview-container ${!isVideoWindowEnabled ? 'video-window-off' : ''}`}
                >
                    <FilteredGuidePreview allPossibleChannels={allPossibleChannels} />
                </div>
            </div>
            <FilteredGuideFooter
                rightSideText={
                    NetworkManager.isNetworkDisconnected()
                        ? ''
                        : Locale.getString(LocaleKeys.GUIDE_SELECT_PRESS_AND_HOLD_MESSAGE)
                }
            />
            <NoNetworkOverlay />
            <Whisper message={NetworkManager.getNetworkDisconnectedMessage()} />
            {isAntiburnOsdActive && <AntiburnOsd />}
            {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                <FilteredGuideInteractiveLayer />
            ) : null}
        </div>
    );
};

export default FilteredGuide;
